import { Controller, useForm } from 'react-hook-form';
import { Image, VStack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import asBentoPage from 'common/bento/hoc/asBentoPage';
import Button from 'components/_core/Button';
import { Checkbox, Field } from 'components/_core/form';
import Heading from 'components/_core/Heading';
import Text from 'components/_core/Text';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';

import cup from './assets/Cup@2x.png';
import locales from './locales';

const researchExperimentSchema = z.object({
  terms: z.literal<boolean>(true, {
    errorMap: () => ({ message: locales.terms.error }),
  }),
});

type ResearchExperimentSchema = z.infer<typeof researchExperimentSchema>;

interface ResearchExperimentProps {
  onAccept: () => void;
  onDecline: () => void;
}

const ResearchExperiment = asBentoPage<ResearchExperimentProps>(
  ({ Layout, onAccept, onDecline, onPrev }) => {
    const { clearErrors, control, formState, handleSubmit } =
      useForm<ResearchExperimentSchema>({
        defaultValues: {
          terms: false,
        },
        mode: 'onSubmit',
        resolver: zodResolver(researchExperimentSchema),
        reValidateMode: 'onSubmit',
      });

    return (
      <Layout nav={<OBNavigation onPrev={onPrev} />}>
        <Image marginX="auto" src={cup} width="180px" />

        <Heading marginY="space-16" size="lg">
          {locales.title}
        </Heading>

        <Text marginBottom="space-24" variant="primary">
          {locales.description}
        </Text>

        <form onChange={() => clearErrors()} onSubmit={handleSubmit(onAccept)}>
          <VStack marginBottom="space-32">
            <Field error={formState.errors.terms?.message}>
              <Checkbox.Group variant="outline">
                <Controller
                  control={control}
                  name="terms"
                  render={({ field }) => (
                    <Checkbox isChecked={field.value} onChange={field.onChange}>
                      <Checkbox.Title>{locales.terms.label}</Checkbox.Title>
                    </Checkbox>
                  )}
                />
              </Checkbox.Group>
            </Field>
          </VStack>

          <Button isExpanded marginBottom="space-16" type="submit">
            {locales.cta}
          </Button>

          <Text
            fontWeight="weight-500"
            marginBottom="space-24"
            variant="secondary"
          >
            {locales.incentive}
          </Text>

          <Button
            icon="arrow-right"
            onClick={onDecline}
            variant="inline-secondary"
          >
            {locales.decline}
          </Button>
        </form>
      </Layout>
    );
  },
);

export default ResearchExperiment;
